import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { updateInvoiceSettings } from '../../../lib/invoiceSettingsMutations';
import { useAuthStore } from '../../../stores/authStore';
import type { CrewBitConfig } from '../../../types/configExport';

export interface DiffSection {
  key: string;
  label: string;
  status: 'will_set' | 'will_change' | 'already_matches' | 'skipped';
  details?: string;
  itemCount?: number;
}

export interface ConfigDiff {
  sections: DiffSection[];
}

async function logActivity(businessId: string, entityType: string, details: string) {
  try {
    await supabase.from('activity_log').insert({
      business_id: businessId,
      activity_type: 'config_import',
      entity_type: entityType,
      description: details,
      entity_id: businessId,
      logged_by: businessId,
    });
  } catch { /* non-critical */ }
}

function validate(raw: unknown): { config: CrewBitConfig | null; errors: string[] } {
  const errors: string[] = [];
  if (!raw || typeof raw !== 'object') {
    errors.push('Invalid JSON — not an object.');
    return { config: null, errors };
  }
  const obj = raw as Record<string, unknown>;
  if (obj._version !== '1.0') {
    errors.push(`Unsupported config version: ${String(obj._version ?? 'missing')}`);
  }
  if (obj.services && !Array.isArray(obj.services)) {
    errors.push('services must be an array.');
  }
  if (obj.hours) {
    const h = obj.hours as Record<string, unknown>;
    if (!h.days || !Array.isArray(h.days)) {
      errors.push('hours.days must be an array.');
    }
  }
  if (errors.length > 0) return { config: null, errors };
  return { config: raw as CrewBitConfig, errors: [] };
}

export function useConfigImport() {
  const business = useAuthStore(s => s.business);
  const businessId = business?.id;
  const mountedRef = useRef(true);

  const [parsed, setParsed] = useState<CrewBitConfig | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [diff, setDiff] = useState<ConfigDiff | null>(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applySummary, setApplySummary] = useState<string[]>([]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const computeDiff = useCallback(async (config: CrewBitConfig): Promise<ConfigDiff> => {
    if (!businessId) return { sections: [] };

    const sections: DiffSection[] = [];

    // Profile
    if (config.profile) {
      const hasProfile = business?.name && business?.address;
      sections.push({
        key: 'profile',
        label: 'Business profile',
        status: hasProfile ? 'will_change' : 'will_set',
        details: hasProfile ? 'Will update profile fields' : 'Will set profile information',
      });
    }

    // Hours
    if (config.hours?.days?.length) {
      const { data: existing } = await supabase
        .from('business_hours')
        .select('id')
        .eq('business_id', businessId)
        .limit(1);
      sections.push({
        key: 'hours',
        label: 'Business hours',
        status: existing && existing.length > 0 ? 'will_change' : 'will_set',
        details: `${config.hours.days.filter(d => d.is_open).length} open days`,
      });
    }

    // Services
    if (config.services?.length) {
      const { data: existing } = await supabase
        .from('services')
        .select('name')
        .eq('business_id', businessId)
        .eq('is_active', true);
      const existingNames = new Set((existing ?? []).map((s: { name: string }) => s.name.toLowerCase()));
      const newServices = config.services.filter(s => !existingNames.has(s.name.toLowerCase()));
      const skippedCount = config.services.length - newServices.length;
      sections.push({
        key: 'services',
        label: 'Services & pricing',
        status: newServices.length > 0 ? (existing && existing.length > 0 ? 'will_change' : 'will_set') : 'already_matches',
        details: newServices.length > 0
          ? `${newServices.length} new service${newServices.length !== 1 ? 's' : ''} will be added${skippedCount > 0 ? `, ${skippedCount} already exist` : ''}`
          : 'All services already exist',
        itemCount: newServices.length,
      });
    }

    // Invoice
    if (config.invoiceSettings) {
      const { data: existing } = await supabase
        .from('invoice_settings')
        .select('id')
        .eq('business_id', businessId)
        .maybeSingle();
      sections.push({
        key: 'invoice',
        label: 'Invoice settings',
        status: existing ? 'will_change' : 'will_set',
        details: existing ? 'Will update invoice branding' : 'Will set invoice defaults',
      });
    }

    // Booking page
    if (config.bookingPage) {
      const { data: existing } = await supabase
        .from('booking_pages')
        .select('id')
        .eq('business_id', businessId)
        .maybeSingle();
      sections.push({
        key: 'booking',
        label: 'Booking page',
        status: existing ? 'will_change' : 'will_set',
        details: existing ? 'Will update booking page settings' : 'Will configure booking page',
      });
    }

    // Financial
    if (config.financialSettings) {
      const { data: existing } = await supabase
        .from('business_financial_settings')
        .select('id')
        .eq('business_id', businessId)
        .maybeSingle();
      sections.push({
        key: 'financial',
        label: 'Financial settings',
        status: existing ? 'will_change' : 'will_set',
        details: existing ? 'Will update financial defaults' : 'Will set financial defaults',
      });
    }

    // Marketing
    if (config.marketing?.automations?.length) {
      const { data: existing } = await supabase
        .from('marketing_automations')
        .select('type')
        .eq('business_id', businessId);
      sections.push({
        key: 'marketing',
        label: 'Marketing automations',
        status: existing && existing.length > 0 ? 'will_change' : 'will_set',
        details: `${config.marketing.automations.length} automation rule${config.marketing.automations.length !== 1 ? 's' : ''}`,
        itemCount: config.marketing.automations.length,
      });
    }

    // Language
    if (config.language) {
      sections.push({
        key: 'language',
        label: 'Language & communication',
        status: business?.client_language ? 'will_change' : 'will_set',
        details: config.language.client_language ? `Set to ${config.language.client_language}` : 'Will update language preferences',
      });
    }

    // AI
    if (config.aiSettings) {
      const { data: existing } = await supabase
        .from('business_ai_settings')
        .select('business_id')
        .eq('business_id', businessId)
        .maybeSingle();
      sections.push({
        key: 'ai',
        label: 'AI features',
        status: existing ? 'will_change' : 'will_set',
        details: config.aiSettings.ai_enabled ? 'AI enabled with feature toggles' : 'AI feature configuration',
      });
    }

    return { sections };
  }, [businessId, business]);

  const parseFile = useCallback(async (file: File) => {
    setApplied(false);
    setApplyError(null);
    setApplySummary([]);
    try {
      const text = await file.text();
      const raw = JSON.parse(text);
      const { config, errors } = validate(raw);
      if (mountedRef.current) {
        setValidationErrors(errors);
        setParsed(config);
        if (config) {
          const d = await computeDiff(config);
          if (mountedRef.current) setDiff(d);
        }
      }
    } catch {
      if (mountedRef.current) {
        setValidationErrors(['Could not parse file as JSON.']);
        setParsed(null);
        setDiff(null);
      }
    }
  }, [computeDiff]);

  const parseStarterPack = useCallback(async (packId: string) => {
    setApplied(false);
    setApplyError(null);
    setApplySummary([]);
    try {
      const { loadStarterPack } = await import('../../../data/starterPacks');
      const config = await loadStarterPack(packId);
      if (!config) {
        if (mountedRef.current) setValidationErrors(['Starter pack not found.']);
        return;
      }
      const { config: validated, errors } = validate(config);
      if (mountedRef.current) {
        setValidationErrors(errors);
        setParsed(validated);
        if (validated) {
          const d = await computeDiff(validated);
          if (mountedRef.current) setDiff(d);
        }
      }
    } catch {
      if (mountedRef.current) {
        setValidationErrors(['Failed to load starter pack.']);
        setParsed(null);
        setDiff(null);
      }
    }
  }, [computeDiff]);

  const applyConfig = useCallback(async () => {
    if (!parsed || !businessId || !diff) return;
    setApplying(true);
    setApplyError(null);
    const summary: string[] = [];
    const errors: string[] = [];

    const actionable = diff.sections.filter(s => s.status === 'will_set' || s.status === 'will_change');

    for (const section of actionable) {
      try {
        switch (section.key) {
          case 'profile': {
            if (!parsed.profile) break;
            const p = parsed.profile;
            const updates: Record<string, unknown> = {};
            if (p.name) updates.name = p.name; if (p.address) updates.address = p.address;
            if (p.city) updates.city = p.city; if (p.province) updates.province = p.province;
            if (p.postal_code) updates.postal_code = p.postal_code;
            if (p.phone) updates.phone = p.phone; if (p.email) updates.email = p.email;
            if (p.timezone) updates.timezone = p.timezone;
            if (Object.keys(updates).length > 0) {
              const { error } = await supabase.from('businesses').update(updates).eq('id', businessId);
              if (error) throw error;
              summary.push('Business profile updated');
              await logActivity(businessId, 'profile', 'Imported business profile');
            }
            break;
          }
          case 'hours': {
            if (!parsed.hours?.days?.length) break;
            await supabase.from('business_hours').delete().eq('business_id', businessId);
            const rows = parsed.hours.days.map(d => ({
              business_id: businessId, day_of_week: d.day_of_week, is_open: d.is_open,
              open_time: d.open_time ?? '09:00', close_time: d.close_time ?? '17:00',
            }));
            const { error } = await supabase.from('business_hours').insert(rows);
            if (error) throw error;
            // User-initiated config import counts as confirming the hours
            // (Today Hub Spec v1 X13) — same stamp as the booking-page case.
            {
              const { error: touchErr } = await supabase
                .from('businesses')
                .update({ business_hours_customized_at: new Date().toISOString() })
                .eq('id', businessId);
              if (touchErr) throw touchErr;
            }
            summary.push('Business hours set');
            await logActivity(businessId, 'hours', `Imported ${rows.filter(r => r.is_open).length} open days`);
            break;
          }
          case 'services': {
            if (!parsed.services?.length) break;
            const { data: existing } = await supabase
              .from('services')
              .select('name')
              .eq('business_id', businessId)
              .eq('is_active', true);
            const existingNames = new Set((existing ?? []).map((s: { name: string }) => s.name.toLowerCase()));
            const newSvcs = parsed.services.filter(s => !existingNames.has(s.name.toLowerCase()));
            if (newSvcs.length > 0) {
              const { data: countData } = await supabase
                .from('services')
                .select('id')
                .eq('business_id', businessId);
              let sortOrder = (countData?.length ?? 0);
              const rows = newSvcs.map(s => ({
                business_id: businessId,
                name: s.name,
                description: s.description ?? null,
                duration_minutes: s.duration_minutes,
                price: s.price,
                category: s.category ?? null,
                is_active: true,
                sort_order: sortOrder++,
                enable_dynamic_pricing: s.enable_dynamic_pricing ?? false,
                min_price: s.min_price ?? null,
                max_price: s.max_price ?? null,
                intro_enabled: s.intro_enabled ?? false,
                intro_price: s.intro_price ?? null,
                intro_label: s.intro_label ?? null,
              }));
              const { error } = await supabase.from('services').insert(rows);
              if (error) throw error;
              summary.push(`${newSvcs.length} service${newSvcs.length !== 1 ? 's' : ''} added`);
              await logActivity(businessId, 'services', `Added ${newSvcs.length} services from config`);
            } else {
              summary.push('Services already exist (skipped)');
            }
            break;
          }
          case 'invoice': {
            if (!parsed.invoiceSettings) break;
            // §Money.HardeningRetirementRefund Phase 4 — direct .upsert/.update
            // retired per Decision #584; routes through update_invoice_settings
            // RPC (Migration 296). The RPC body's COALESCE-extends pattern + its
            // INSERT-when-absent branch covers both create + update flows that
            // the previous code split between .insert / .update. Only the
            // brand-only keep-set crosses the wire (legacy 9-column shape from
            // older config files is naturally filtered by the wrapper's typed
            // arg surface — no round-trip of retired tax_rate / payment_terms).
            // §Settings Phase 2B: accepted_methods replaced accept_etransfer.
            // Back-compat: older exports may carry accept_etransfer (boolean) —
            // reconstruct accepted_methods including 'etransfer' if so.
            const legacyAcceptEtransfer = (parsed.invoiceSettings as { accept_etransfer?: boolean }).accept_etransfer;
            const acceptedMethods = parsed.invoiceSettings.accepted_methods
              ?? (legacyAcceptEtransfer === true ? ['card', 'etransfer', 'cash'] : undefined);
            const result = await updateInvoiceSettings({
              businessId,
              logoUrl: parsed.invoiceSettings.logo_url,
              accentColor: parsed.invoiceSettings.accent_color,
              invoicePrefix: parsed.invoiceSettings.invoice_prefix,
              defaultNote: parsed.invoiceSettings.default_note,
              defaultThankYou: parsed.invoiceSettings.default_thank_you,
              acceptedMethods: acceptedMethods as ('card' | 'etransfer' | 'cash')[] | undefined,
              etransferEmail: parsed.invoiceSettings.etransfer_email,
            });
            if (!result.success) throw new Error(`update_invoice_settings: ${result.error.code}`);
            summary.push('Invoice settings updated');
            await logActivity(businessId, 'invoice_settings', 'Imported invoice branding');
            break;
          }
          case 'booking': {
            if (!parsed.bookingPage) break;
            const { data: existing } = await supabase
              .from('booking_pages')
              .select('id')
              .eq('business_id', businessId)
              .maybeSingle();
            if (existing) {
              const { error } = await supabase.from('booking_pages').update(parsed.bookingPage).eq('business_id', businessId);
              if (error) throw error;
            } else {
              const { error } = await supabase.from('booking_pages').insert({ business_id: businessId, ...parsed.bookingPage });
              if (error) throw error;
            }
            // User-initiated config import counts as customizing the booking
            // page — flip get_setup_progress.has_booking_page. Migration 132.
            {
              const { error: touchErr } = await supabase
                .from('businesses')
                .update({ booking_page_customized_at: new Date().toISOString() })
                .eq('id', businessId);
              if (touchErr) throw touchErr;
            }
            summary.push('Booking page updated');
            await logActivity(businessId, 'booking_page', 'Imported booking page config');
            break;
          }
          case 'financial': {
            if (!parsed.financialSettings) break;
            const { data: existing } = await supabase
              .from('business_financial_settings')
              .select('id')
              .eq('business_id', businessId)
              .maybeSingle();
            if (existing) {
              const { error } = await supabase.from('business_financial_settings').update(parsed.financialSettings).eq('business_id', businessId);
              if (error) throw error;
            } else {
              const { error } = await supabase.from('business_financial_settings').insert({ business_id: businessId, ...parsed.financialSettings });
              if (error) throw error;
            }
            summary.push('Financial settings updated');
            await logActivity(businessId, 'financial_settings', 'Imported financial settings');
            break;
          }
          case 'marketing': {
            if (!parsed.marketing?.automations?.length) break;
            for (const auto of parsed.marketing.automations) {
              const { data: existing } = await supabase
                .from('marketing_automations')
                .select('id')
                .eq('business_id', businessId)
                .eq('type', auto.type)
                .maybeSingle();
              const payload = {
                type: auto.type,
                enabled: auto.enabled ?? false,
                days_threshold: auto.days_threshold ?? 30,
                subject: auto.subject ?? '',
                body: auto.body ?? '',
                include_booking_link: auto.include_booking_link ?? false,
                include_intro_offer: auto.include_intro_offer ?? false,
                updated_at: new Date().toISOString(),
              };
              if (existing) {
                await supabase.from('marketing_automations').update(payload).eq('id', existing.id);
              } else {
                await supabase.from('marketing_automations').insert({ business_id: businessId, ...payload });
              }
            }
            summary.push(`${parsed.marketing.automations.length} marketing rule${parsed.marketing.automations.length !== 1 ? 's' : ''} set`);
            await logActivity(businessId, 'marketing', `Imported ${parsed.marketing.automations.length} automations`);
            break;
          }
          case 'language': {
            if (!parsed.language) break;
            const updates: Record<string, unknown> = {};
            if (parsed.language.client_language) updates.client_language = parsed.language.client_language;
            if (parsed.language.auto_translate_messages != null) updates.auto_translate_messages = parsed.language.auto_translate_messages;
            if (Object.keys(updates).length > 0) {
              const { error } = await supabase.from('businesses').update(updates).eq('id', businessId);
              if (error) throw error;
              summary.push('Language settings updated');
              await logActivity(businessId, 'language', 'Imported language settings');
            }
            break;
          }
          case 'ai': {
            if (!parsed.aiSettings) break;
            const { data: existing } = await supabase
              .from('business_ai_settings')
              .select('business_id')
              .eq('business_id', businessId)
              .maybeSingle();
            if (existing) {
              const { error } = await supabase.from('business_ai_settings').update(parsed.aiSettings).eq('business_id', businessId);
              if (error) throw error;
            } else {
              const { error } = await supabase.from('business_ai_settings').insert({ business_id: businessId, ...parsed.aiSettings });
              if (error) throw error;
            }
            summary.push('AI settings updated');
            await logActivity(businessId, 'ai_settings', 'Imported AI feature settings');
            break;
          }
        }
      } catch (e) {
        errors.push(`${section.label}: ${e instanceof Error ? e.message : 'failed'}`);
      }
    }

    if (mountedRef.current) {
      setApplySummary(summary);
      setApplied(true);
      setApplying(false);
      if (errors.length > 0) {
        setApplyError(`Partial success. Failed: ${errors.join('; ')}`);
      }
    }
  }, [parsed, businessId, diff]);

  const reset = useCallback(() => {
    setParsed(null);
    setValidationErrors([]);
    setDiff(null);
    setApplied(false);
    setApplyError(null);
    setApplySummary([]);
  }, []);

  return {
    parseFile,
    parseStarterPack,
    parsed,
    validationErrors,
    diff,
    applyConfig,
    applying,
    applied,
    applyError,
    applySummary,
    reset,
  };
}
